<script setup lang="ts">
import { __ } from '@@/bits/intl'
import OzLoadingSpinnerWithCheckmark from '@@/library/v4/components/OzLoadingSpinnerWithCheckmark.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { computed, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    progress?: number | null
    done: boolean
    fileName?: string
    canvasWidth?: number
    type?: 'file' | 'link'
  }>(),
  {
    progress: null,
    done: false,
    fileName: '',
    canvasWidth: window.innerWidth,
    type: 'file',
  },
)
const emit = defineEmits(['clear', 'cancel'])
const isUploadDone = computed(() => props.done)
const isTransformedToCheckmark = ref(false)
const isHidden = ref(false)
const isPeeking = ref(false)

const progressingText = computed(() => {
  if (props.type === 'file') {
    return __('Uploading')
  }
  return __('Posting')
})

const doneText = computed(() => {
  if (props.type === 'file') {
    return __('Uploaded')
  }
  return __('Posted')
})

function handleTransformedToCheckmark() {
  isTransformedToCheckmark.value = true
}

watch(isUploadDone, (newValue) => {
  if (!newValue) {
    isTransformedToCheckmark.value = false
  }
})
watch(isTransformedToCheckmark, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      hide()
    }, 3000)
    setTimeout(() => {
      emit('clear')
    }, 4500)
  }
})
function hide() {
  isHidden.value = true
}

const stopPeekingTimer = ref<ReturnType<typeof setTimeout> | null>(null)
function peek() {
  // N.B. This is commented out because it's not used in the current implementation.
  // isPeeking.value = true
  // if (stopPeekingTimer.value) {
  //   clearTimeout(stopPeekingTimer.value)
  // }
}
function stopPeeking() {
  stopPeekingTimer.value = setTimeout(() => {
    isPeeking.value = false
  }, 1000)
}

function cancelStoppingPeeking() {
  if (stopPeekingTimer.value) {
    clearTimeout(stopPeekingTimer.value)
  }
}
defineExpose({
  stopPeeking,
  cancelStoppingPeeking,
})
</script>

<template>
  <div
    :class="[
      'h-6',
      'transition-all',
      'duration-300',
      'ease-in-out',
      'min-w-[20px]',
      {
        'pe-1': !isHidden || isPeeking,
        'pe-0.5': isHidden && !isPeeking,
      },
      'ps-0.5',
      'bg-dark-text-200',
      'backdrop-blur-10',
      'rounded-xl',
      'text-light-text-100',
      'text-body-small',
      'flex',
      'items-center',
      'select-none',
      'overflow-hidden',
    ]"
    :style="{
      'max-width': !isHidden || isPeeking ? `${props.canvasWidth}px` : '0px',
    }"
    @pointerover="peek"
  >
    <div
      :class="['w-5', 'h-5', 'rounded-full', 'bg-dark-ui-100', 'flex', 'items-center', 'justify-center', 'shrink-0']"
    >
      <OzLoadingSpinnerWithCheckmark
        :progress="props.progress"
        :class="['shrink-0']"
        :size="12"
        :color="'white'"
        :is-checkmark="isUploadDone"
        :spinner-label="progressingText"
        :checkmark-label="doneText"
        @transformed-to-checkmark="handleTransformedToCheckmark"
      />
    </div>
    <Transition
      leave-active-class="transition-all delay-100 duration-50 ease-in motion-reduce:transition-none"
      leave-to-class="opacity-0 max-w-0"
      leave-class="max-w-6xl"
    >
      <div v-if="!isHidden || isPeeking" class="ms-2">
        <span v-if="!isTransformedToCheckmark" class="truncate w-full">{{ progressingText }}</span>
        <span v-else class="truncate w-full">{{ doneText }}</span>
      </div>
    </Transition>
    <div
      :class="{
        'w-0.5': !isTransformedToCheckmark,
        'animate-to-w-2 w-2': isTransformedToCheckmark && (!isHidden || isPeeking),
        'w-2': isHidden && !isPeeking,
      }"
    ></div>
    <Transition
      leave-active-class="transition-all duration-500 ease-in motion-reduce:transition-none"
      leave-to-class="opacity-0 max-w-0"
      leave-class="max-w-6xl"
    >
      <OzPlainButton
        v-if="!isTransformedToCheckmark"
        :dark-mode="true"
        :text="__('Cancel')"
        :color-scheme="OzPlainButtonColorScheme.Secondary"
        :size-preset="OzPlainButtonSizePreset.H24px"
        @click="emit('cancel')"
      />
    </Transition>
  </div>
</template>

<style scoped lang="postcss">
/*
  Delay 0.4s to wait for the cancel button to start disappearing.
*/
.animate-to-w-2 {
  animation: to-w-2 0.1s ease-in 0.4s forwards;
}
@keyframes to-w-2 {
  to {
    width: 8px;
  }
}
</style>
